
// planet-aspects.js
// Controller per la visualizzazione degli ASPETTI planetari (storico e futuro).
// - Aspetti supportati: Congiunzione(0), Sestile(60), Quadratura(90), Trigono(120), Opposizione(180)
// - Orbita configurabile (default 3°)
// - Retrogradazioni opzionali per pianeta (intervalli R/D)
// - Disegno: linee verticali START/END + etichette centrate, anti-collisione
// - Include API per statistiche su intero dataset (intervals + OHLC).
//
// Requisiti:
//  - parseEphemeridesCSV(csvText) disponibile (riusa quella che hai già per gli ingressi).
//  - Modello PLANET KEYS come per ingressi: 'SOLE','LUNA','MERCURIO','VENERE','MARTE','GIOVE','SATURNO','URANO','NETTUNO'.
//
// Integrazione: vedi sezione "Come integrare" in fondo.

import { getEphemeridesRows } from './ephemerides-provider.js';

const CSV_URL = '/assets/effemeridi.csv'; // <-- aggiorna path


export const PLANET_KEYS = ['SOLE','LUNA','MERCURIO','VENERE','MARTE','GIOVE','SATURNO','URANO','NETTUNO'];
export const PLANET_ICON = { SOLE:'☉', LUNA:'☽', MERCURIO:'☿', VENERE:'♀', MARTE:'♂', GIOVE:'♃', SATURNO:'♄', URANO:'♅', NETTUNO:'♆' };

const ASPECTS = { CONJ:0, SEXT:60, SQUARE:90, TRINE:120, OPP:180 };
const ASPECT_LABEL = { 
    CONJ:'☌', 
    SEXT:'✶', 
    SQUARE:'□', 
    TRINE:'△', 
    OPP:'☍',
    RETRO:'℞',  // per la retrogradazione 
};
// Palette per TIPO di aspetto (richiesta)
const ASPECT_COLOR = {
  CONJ:'#FF9800',   // arancione
  SEXT:'#4CAF50',   // verde
  SQUARE:'#EF5350', // rosso
  TRINE:'#00BCD4',  // azzurro
  OPP:'#3F51B5',    // indaco
  
  // colore dedicato alla retrogradazione (scegli tu la tinta)
  RETRO:'#9C27B0'   // viola (esempio)

};


// --- Utilities geometriche/angolari (riusano il tuo stile) ---
const mod360 = d => ((d % 360) + 360) % 360;
// delta continuo nel senso breve (-180..+180]
function deltaContinuous(a, b) {
  let d = mod360(b) - mod360(a);
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}
// differenza angolare minima [0..180]
function angleMin180(aDeg, bDeg) {
  const d = Math.abs(mod360(bDeg) - mod360(aDeg));
  return d > 180 ? 360 - d : d;
}

// Gruppo per timestamp (secondi UTC)
function groupEventsByTime(items) {
  const m = new Map();
  for (const it of items) {
    if (!m.has(it.time)) m.set(it.time, []);
    m.get(it.time).push(it);
  }
  return m;
}



// Normalizza OHLC in forma {time(sec), open, high, low, close}
function normalizeInjectedOhlc(arr) {
  try {
    return arr.map(d => {
      let t = d.time;
      if (typeof t === 'string') t = Math.floor(new Date(t).getTime()/1000);
      if (typeof t === 'number') t = (t > 1e12) ? Math.floor(t/1000) : t;
      return {
        time: Number(t),
        open: Number(d.open), high: Number(d.high),
        low:  Number(d.low),  close:Number(d.close)
      };
    }).filter(b => Number.isFinite(b.time) && Number.isFinite(b.close));
  } catch (e) {
    console.warn('OHLC injection non valida; ignoro.', e);
    return null;
  }
}


// Prova a ottenere un range {from, to} robusto per il pane corrente
function getSafePanePriceRange(chart, fallback = { from: 0, to: 180 }) {
  try {
    // 1) prova chart.getVisiblePriceRange() (alcune build ritornano null)
    const pr = chart.getVisiblePriceRange?.();
    if (pr && Number.isFinite(pr.from) && Number.isFinite(pr.to) && pr.to > pr.from) return pr;

    // 2) prova con le scale del primo pane
    const panes = chart.getPanes?.();
    const pane = Array.isArray(panes) ? panes[0] : null;
    if (pane && typeof pane.getLeftPriceScales === 'function') {
      const left = pane.getLeftPriceScales() || [];
      for (const s of left) {
        if (typeof s.getVisiblePriceRange === 'function') {
          const r = s.getVisiblePriceRange();
          if (r && Number.isFinite(r.from) && Number.isFinite(r.to) && r.to > r.from) return r;
        }
      }
    }
    if (pane && typeof pane.getRightPriceScales === 'function') {
      const right = pane.getRightPriceScales() || [];
      for (const s of right) {
        if (typeof s.getVisiblePriceRange === 'function') {
          const r = s.getVisiblePriceRange();
          if (r && Number.isFinite(r.from) && Number.isFinite(r.to) && r.to > r.from) return r;
        }
      }
    }
  } catch (e) {
    // ignora e passa al fallback
  }
  // 3) fallback “metrico”: range fisso
  return fallback;
}


// Converte un testo in "verticale": un carattere per riga.
// Mantiene intatti gli \n già presenti e divide anche per spazi (compattando).
function toVerticalText(str) {
  // separa in righe pre-esistenti, poi ogni riga in caratteri
  const lines = String(str).split('\n');
  const out = [];
  for (const ln of lines) {
    const s = ln.replace(/\s+/g, ' ').trim();
    // se vuoi separare parola per parola invece che lettera, usa s.split(' ')
    for (const ch of [...s]) out.push(ch);
    // inserisce una riga vuota tra le righe originali per separarle visivamente
    out.push(''); 
  }
  // rimuove l’ultima riga vuota aggiunta
  if (out.length && out[out.length - 1] === '') out.pop();
  return out.join('\n');
}


// Y dello stack verticale (in alto nel pane), parametrico
function computeStackYPositions(chart, count, padTopPct = 0.10, deltaPct = 0.06) {
  const pr = chart.getVisiblePriceRange();
  const H  = pr.to - pr.from;
  const padTop = H * padTopPct;
  const delta  = H * deltaPct;
  const base   = pr.to - padTop;
  const ys = [];
  const start = base - (delta * (count - 1) / 2);
  for (let i = 0; i < count; i++) ys.push(start + i * delta);
  return ys;
}

// Interpolazione sull’attraversamento della soglia f(t) = bound (bound ±orb)
function interpolateAspectTime(t1, a1, b1, t2, a2, b2, target, bound) {
  const f1 = angleMin180(a1, b1) - target;
  const f2 = angleMin180(a2, b2) - target;
  const denom = (f2 - f1);
  const frac = denom === 0 ? 0 : (bound - f1) / denom;
  const f = Math.max(0, Math.min(1, frac));
  return Math.round(t1 + f * (t2 - t1));
}

/**
 * Intervalli d'aspetto A-B per un "target" (0/60/90/120/180) con orbita 'orbDeg'
 * Ritorna: [{ start, end, aspectKey, pair }]
 */
function computeAspectIntervals(rows, planetA, planetB, aspectKey, orbDeg, from = -Infinity, to = Infinity) {
  const target = ASPECTS[aspectKey]; if (target == null) return [];
  const out = [];
  let inside = false, startT = null;

  for (let i = 1; i < rows.length; i++) {
    const r1 = rows[i - 1], r2 = rows[i];
    const a1 = r1.values[planetA], b1 = r1.values[planetB];
    const a2 = r2.values[planetA], b2 = r2.values[planetB];
    if (!Number.isFinite(a1) || !Number.isFinite(b1) || !Number.isFinite(a2) || !Number.isFinite(b2)) continue;

    const d1 = angleMin180(a1, b1), d2 = angleMin180(a2, b2);
    const in1 = Math.abs(d1 - target) <= orbDeg;
    const in2 = Math.abs(d2 - target) <= orbDeg;

    if (!inside && in2) {
      const bound = (d1 - target) > (d2 - target) ? +orbDeg : -orbDeg;
      startT = interpolateAspectTime(r1.time, a1, b1, r2.time, a2, b2, target, bound);
      inside = true;
    } else if (inside && !in2) {
      const bound = (d2 - target) > (d1 - target) ? +orbDeg : -orbDeg;
      const endT = interpolateAspectTime(r1.time, a1, b1, r2.time, a2, b2, target, bound);
      if (startT != null) {
        if (endT >= from && startT <= to) {
          out.push({ start: startT, end: endT, aspectKey, pair: `${planetA}-${planetB}` });
        }
      }
      inside = false; startT = null;
    }
  }
  if (inside && startT != null) {
    const last = rows[rows.length - 1];
    out.push({ start: startT, end: last.time, aspectKey, pair: `${planetA}-${planetB}` });
  }
  return out;
}

/** Intervalli di retrogradazione per pianeta: [{ start, end, planet }] */
function computeRetrogradeIntervals(rows, planetKey, from = -Infinity, to = Infinity) {
  const out = [];
  let retro = false, startT = null;

  for (let i = 1; i < rows.length; i++) {
    const r0 = rows[i - 2], r1 = rows[i - 1], r2 = rows[i];
    const l0 = r0?.values?.[planetKey], l1 = r1.values[planetKey], l2 = r2.values[planetKey];
    if (!Number.isFinite(l1) || !Number.isFinite(l2)) continue;

    const v1 = Number.isFinite(l0) ? deltaContinuous(l0, l1) : 0;
    const v2 = deltaContinuous(l1, l2);

    if (!retro && v2 < 0 && v1 >= 0) { startT = r1.time; retro = true; }
    else if (retro && v2 > 0 && v1 <= 0) {
      const endT = r1.time;
      if (endT >= from && startT <= to) out.push({ start: startT, end: endT, planet: planetKey });
      retro = false; startT = null;
    }
  }
  if (retro && startT != null) {
    const last = rows[rows.length - 1];
    out.push({ start: startT, end: last.time, planet: planetKey });
  }
  return out;
}

// Disegno etichetta centrata con anchored_text
async function drawAnchoredLabel(chart, time, y, text, color, fs = 16, opts = {}) {
  const vertical = !!opts.vertical;
  const wrapWidth = Number.isFinite(opts.wordWrapWidth) ? opts.wordWrapWidth : 20; // sicurezza

  const finalText = vertical ? toVerticalText(text) : text;

  const id = await Promise.resolve(
    chart.createShape(
      { time, price: y }, // PricedPoint: centrato su X=time
      {
        shape: 'anchored_text',
        text: finalText,
        lock: true,
        overrides: {
          text: finalText,
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif',
          fontsize: fs,
          bold: false,
          color,
          fillBackground: false,
          drawBorder: false,
          fixedSize: true,
          horzLabelsAlign: 'center', // allineamento orizzontale
          // forziamo wrapping (non dovrebbe servire per testo verticale, ma è un safety)
          wordWrap: true,
          wordWrapWidth: wrapWidth
        },
        zOrder: 'top'
      }
    )
  );

  return id;
}


// Clear "safe": rimuovi tutto ciò che abbiamo creato
async function clearAspectsDrawings(chart, ref) {
  if (!ref.ids || ref.ids.size === 0) return;
  const toRemove = await Promise.all([...ref.ids].map(id => Promise.resolve(id)));
  for (const id of toRemove) {
    try { chart.removeEntity(id); } catch {}
  }
  ref.ids.clear();
}


// Disegna gli intervalli: start/end line + label centrate (anti-collisione per timestamp)
async function drawAspectIntervals(chart, intervals, ref, orbDeg, labelFs = 16) {
  const pts = [];
  for (const itv of intervals) {
    pts.push({ time: itv.start, type: 'start', itv });
    pts.push({ time: itv.end,   type: 'end',   itv });
  }
  pts.sort((a,b) => a.time - b.time);

  const groups = groupEventsByTime(pts);

  for (const [t, arr] of groups.entries()) {
    // Una sola vertical line per timestamp
    const firstKey = arr[0].itv.aspectKey;
    const col = ASPECT_COLOR[firstKey] || '#888';
    const title = `Aspetti (${arr.length})`;
    const lineId = await chart.createMultipointShape([{ time: t }], {
      shape: 'vertical_line', text: title, lock: true,
      overrides: { color: col, linestyle: 0, linewidth: 2 },
      zOrder: 'top'
    });
    ref.ids.add(lineId);

    // Stack verticale con distanza maggiore
    const ys = computeStackYPositions(
      chart, arr.length,
      (ref.collision?.padTopPct ?? 0.10),
      (ref.collision?.deltaPct ?? 0.08) // ↑ un po’ più ampia di prima
    );

    for (let i = 0; i < arr.length; i++) {
        const { itv, type } = arr[i];
        const aspKey = itv.aspectKey;
        const icon = ASPECT_LABEL[aspKey] || aspKey;
        const color = ASPECT_COLOR[aspKey] || '#888';

        if (aspKey === 'RETRO') {
            // Etichetta retrogradazione (singolo pianeta)
            // Esempio verticale: ℞ RETRO START\nMERCURIO
            //const label = `${icon} RETRO ${type.toUpperCase()}\n${itv.pair}`;
            const label = `${icon} RETRO ${type.toUpperCase()}\n`;
            const lid = await drawAnchoredLabel(chart, t, ys[i], label, color, Math.max(12, labelFs), { vertical: true, wordWrapWidth: 14 });
            ref.ids.add(lid);
        } else {
            // Etichetta aspetto angolare (due pianeti)
            // Esempio: ☌ START\nVENERE-MARTE\n(orb 3°)
            //const label = `${icon} ${type.toUpperCase()}\n${itv.pair}\n(orb ${orbDeg}°)`;
            const label = `${icon} ${type.toUpperCase()}\n${itv.pair}\n`;
            const lid = await drawAnchoredLabel(chart, t, ys[i], label, color, Math.max(12, labelFs), { vertical: true, wordWrapWidth: 16 });
            ref.ids.add(lid);
        }
    }
  }
}


// Controller principale: initPlanetAspects
export async function initPlanetAspects(widget, options = {}) {
  const {
    csvUrl = CSV_URL,
    selectedPairs = [],                      // es. [ ['VENERE','MARTE'], ['SOLE','SATURNO'] ]
    selectedAspects = ['CONJ','SEXT','SQUARE','TRINE','OPP'],
    orbDeg = 3,
    retroPlanets = [],                       // es. ['MERCURIO','VENERE']
    labelFontSize = 16,
    ohlc: injectedOhlc = null       // injection OHLC opzionale
  } = options;

  const chart = widget.activeChart();

  // Carica effemeridi con il parser che già usi
  /*
  const csvText = await fetch(csvUrl).then(r => r.text());
  if (typeof parseEphemeridesCSV !== 'function') {
    throw new Error('parseEphemeridesCSV non è disponibile. Assicurati che sia caricato (planet-ingresses.js).');
  }
  const rows = parseEphemeridesCSV(csvText);
  */
  const rows = await getEphemeridesRows(csvUrl); // utilizza ephemerides-provider per eseguire una sola volta il parsing e poi condividere le rows
  
  // cache per statistiche/global
  let intervalsAllCache = null;
  let ohlcAllCache = Array.isArray(injectedOhlc) ? normalizeInjectedOhlc(injectedOhlc) : null; 

  // cache per disegno visibile
  let intervalsVisibleCache = [];

  // Stato interno
  const ref = {
    ids: new Set(),
    sub: null,
    active: false,
    lock: Promise.resolve(),
    // parametri anti-collisione
    collision: { padTopPct: 0.10, deltaPct: 0.06, align: 'center' },
    // parametri UI correnti
    pairs: selectedPairs.slice(),
    aspects: selectedAspects.slice(),
    orb: orbDeg,
    retro: retroPlanets.slice(),
    labelFs: labelFontSize,
    highlightDuration: false
  };

  function invalidateGlobalIntervals() {
    intervalsAllCache = null;
  }
  
    // Calcolo intervalli su INTERO DATASET (per statistiche)
  async function computeAllIntervals() {
    if (intervalsAllCache) return intervalsAllCache;
    const all = [];

    // estremi dataset
    const tMin = rows[0]?.time ?? -Infinity;
    const tMax = rows[rows.length - 1]?.time ?? +Infinity;

    // aspetti angolari
    for (const pair of ref.pairs) {
      const [A, B] = pair;
      if (!PLANET_KEYS.includes(A) || !PLANET_KEYS.includes(B)) continue;
      for (const aKey of ref.aspects) {
        all.push(...computeAspectIntervals(rows, A, B, aKey, ref.orb, tMin, tMax));
      }
    }
    // retrogradazioni
    for (const p of ref.retro) {
      if (!PLANET_KEYS.includes(p)) continue;
      all.push(...computeRetrogradeIntervals(rows, p, tMin, tMax));
    }

    intervalsAllCache = all;
    return all;
  }

  // Caricamento OHLC su INTERO DATASET (best effort)
  async function loadOhlcAll() {
    if (ohlcAllCache) return ohlcAllCache;

    // 1) Injection: se l'hai fornita in options, usala.
    if (Array.isArray(injectedOhlc)) {
      ohlcAllCache = normalizeInjectedOhlc(injectedOhlc) || [];
      return ohlcAllCache;
    }
    // 2) Fallback: exportData (se la tua build lo supporta)
    try {
      const data = await chart.exportData?.({ includeTime: true });
      if (Array.isArray(data) && data.length) {
        ohlcAllCache = data.map(d => {
          let t = d.time;
          if (typeof t === 'string') t = Math.floor(new Date(t).getTime() / 1000);
          if (typeof t === 'number') t = (t > 1e12) ? Math.floor(t / 1000) : t;
          return {
            time: Number(t),
            open: Number(d.open), high: Number(d.high),
            low:  Number(d.low),  close:Number(d.close)
          };
        }).filter(b => Number.isFinite(b.time) && Number.isFinite(b.close));
        return ohlcAllCache;
      }
    } catch (e) {
      console.warn('exportData non disponibile o fallita.', e);
    }
    ohlcAllCache = [];
    return ohlcAllCache;
  }
  
  async function render(range) {
        ref.lock = Promise.resolve(ref.lock).then(async () => {
        await clearAspectsDrawings(chart, ref);

        const intervals = [];

        // aspetti tra coppie
        for (const pair of ref.pairs) {
            const [A, B] = pair;
            if (!PLANET_KEYS.includes(A) || !PLANET_KEYS.includes(B)) continue;
            for (const aspKey of ref.aspects) {
            intervals.push(...computeAspectIntervals(rows, A, B, aspKey, ref.orb, range.from, range.to));
            }
        }

        // retrogradazioni
        for (const p of ref.retro) {
            if (!PLANET_KEYS.includes(p)) continue;
            const retro = computeRetrogradeIntervals(rows, p, range.from, range.to);
            //for (const r of retro) intervals.push({ start: r.start, end: r.end, aspectKey: 'CONJ', pair: `${p}-R` }); // usa colore CONJ per retro, o definisci palette dedicata
            for (const r of retro) intervals.push({ start: r.start, end: r.end, aspectKey: 'RETRO', pair: p });
        }

        intervalsVisibleCache = intervals; // cache per statistiche

        // evidenzia durata: disegna banda (rettangolo) tra START e END in fondo al pane
        if (ref.highlightDuration) {
            // Prendi un range robusto; puoi cambiare il fallback se vuoi (es. 0..100)
            const pr = getSafePanePriceRange(chart, { from: 0, to: 180 });
            const top = pr.to;
            const bottom = pr.from;

            for (const itv of intervals) {
                const color = ASPECT_COLOR[itv.aspectKey] || '#888';

                // Nota: i rettangoli multipunto vanno creati con createMultipointShape
                const rectId = await Promise.resolve(
                chart.createMultipointShape(
                    [
                    { time: itv.start, price: top },    // vertice alto-sinistra
                    { time: itv.end,   price: bottom }  // vertice basso-destra
                    ],
                    {
                    shape: 'rectangle',
                    lock: true,
                    overrides: {
                        color: color,            // colore bordo/riempimento del tool
                        fillBackground: true,
                        backgroundColor: color,  // stessa tinta dell’aspetto
                        transparency: 85,        // banda molto leggera (0 opaco, 100 invisibile)
                        // Se la tua build usa backgroundTransparency:
                        // backgroundTransparency: 85
                    },
                    zOrder: 'bottom'
                    }
                )
                );

                ref.ids.add(rectId);
            }
        }


      await drawAspectIntervals(chart, intervals, ref, ref.orb, ref.labelFs);
    });
    return ref.lock;
  }

  function subscribeRange() {
    if (ref.sub) return;
    const sub = chart.onVisibleRangeChanged();
    const handler = (r) => { render(r); };
    sub.subscribe(ref, handler);
    ref.sub = { sub, handler };
  }
  function unsubscribeRange() {
    if (!ref.sub) return;
    ref.sub.sub.unsubscribeAll(ref);
    ref.sub = null;
  }

  async function show() {
    if (ref.active) return;
    ref.active = true;
    await render(chart.getVisibleRange());
    subscribeRange();
  }

  async function hide() {
    if (!ref.active) return;
    ref.active = false;
    unsubscribeRange();
    await Promise.resolve(ref.lock);
    await clearAspectsDrawings(chart, ref);
  }

    // [OPZIONALE] Aggiorna OHLC iniettato a runtime
    async function setOhlcAll(arr) {
        ohlcAllCache = normalizeInjectedOhlc(arr) || [];
    }

  return {
    show, hide, toggle: async () => (ref.active ? hide() : show()),
    dispose: async () => { unsubscribeRange(); await Promise.resolve(ref.lock); await clearAspectsDrawings(chart, ref); ref.active = false; },
    isActive: () => ref.active,

    // setters dalla UI
    setPairs: async (pairs) => { ref.pairs = pairs.slice(); if (ref.active) await render(chart.getVisibleRange()); },
    setAspects: async (list) => { ref.aspects = list.slice(); if (ref.active) await render(chart.getVisibleRange()); },
    setOrb: async (deg) => { ref.orb = Number(deg) || 3; if (ref.active) await render(chart.getVisibleRange()); },
    setRetroPlanets: async (list) => { ref.retro = list.slice(); if (ref.active) await render(chart.getVisibleRange()); },
    setHighlightDuration: async (flag) => {
        ref.highlightDuration = !!flag;
        if (ref.active) await render(chart.getVisibleRange());
    },

    // opzionali (anti-collisione e stile)
    setCollision: async ({ padTopPct, deltaPct } = {}) => {
      if (Number.isFinite(padTopPct)) ref.collision.padTopPct = padTopPct;
      if (Number.isFinite(deltaPct))  ref.collision.deltaPct  = deltaPct;
      if (ref.active) await render(chart.getVisibleRange());
    },
    setLabelFontSize: async (fs) => { ref.labelFs = Number(fs) || 16; if (ref.active) await render(chart.getVisibleRange()); },

    // Dati per statistica (GLOBAL, async)
    getAllIntervals: async () => await computeAllIntervals(),
    getOhlcAll: async () => await loadOhlcAll(),

    // (facoltativo) visibile corrente
    getVisibleIntervals: () => intervalsVisibleCache.slice(),

    // [OPZIONALE] injection OHLC a runtime
    setInjectedOhlc: async (arr) => { await setOhlcAll(arr); },

  };
}

/* ===========================
   Come integrare (promemoria)
   ===========================
import { initPlanetAspects } from './planet-aspects.js';

widget.onChartReady(async () => {
  const aspectsCtrl = await initPlanetAspects(widget, {
    csvUrl: '/assets/effemeridi_sessadecimali_2010_2050.csv',
    selectedPairs: [],                   // es. [['VENERE','MARTE']]
    selectedAspects: ['CONJ','SQUARE'],  // es. []
    orbDeg: 3,
    retroPlanets: []                     // es. ['MERCURIO']
  });

  // poi collega la UI (vedi planet-aspects.ui.js)
});
*/
